import parsePhoneNumberFromString from 'libphonenumber-js';
import type { IntlShape } from 'react-intl/src/types';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import type { Country } from '@/types/regionTypes';

import type { CountryCode } from '@/graphql/saleor/api';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import type { StoreHit } from '@/integrations/algolia/types';
import { reorderStores } from '@/lib/boutique/helpers';
import { EMAIL_REGX, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';
import { createSearchOptions } from '@/views/AppointmentView/helpers';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';

import { messages } from './messages';

export const getStores = async (
  locale: string,
  countryCode: CountryCode,
  localeSlug: string,
  facetFilters?:
    | string
    | readonly string[]
    | ReadonlyArray<readonly string[] | string>,
) => {
  const index = algoliaSearchClient.initIndex(getIndexName('STORE', locale));
  const searchOptions = createSearchOptions({
    countryCode,
    localeSlug,
    webVisible: true,
  });
  const { hits } = await index.search('', {
    ...searchOptions,
    facetFilters,
  });

  return reorderStores(hits as StoreHit[]);
};

export type AvailabilityRequestSchema = InferType<
  ReturnType<typeof availabilityRequestSchema>
>;

export const availabilityRequestSchema = (
  formatMessage: IntlShape['formatMessage'],
  country: Country,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const invalidPhoneMsg = formatMessage(formMessages.invalidPhone);
  return object({
    title: getTitleSchema(formatMessage, country.code),
    firstName: getNameSchema(formatMessage, 'first'),
    lastName: getNameSchema(formatMessage, 'last'),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(''),
    phonePrefix: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(getPhonePrefixSelectOptByCode(country.code)),
    phone: string()
      .max(
        MAX_CHAR_PHONE,
        formatMessage(formMessages.maxChars, {
          max: MAX_CHAR_PHONE,
        }),
      )
      .when(['phonePrefix'], {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix,
        then: (schema) =>
          schema.test('is-valid-phone', invalidPhoneMsg, function (value) {
            if (!value) {
              return true;
            }
            const { phonePrefix } = this.parent;
            const fullPhoneNumber = `${phonePrefix.value}${value}`;
            const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
            return phoneNumber ? phoneNumber.isValid() : false;
          }),
        otherwise: () => string(),
      })
      .default(''),
    newsletterOptIn: bool().default(false),
    comments: string()
      .max(3000, formatMessage(messages.maxCommentsMsg))
      .default(''),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
